/**
 * RUN 84 - the bridge to the TMS Push extension, and the ONLY code in the web app that touches
 * `chrome.runtime`.
 *
 * Authority: `12-tms-push-extension.md` section 6 (the page <-> extension protocol, version 1),
 * section 6a (the result the API accepts) and section 6b (as built: the extension's `refused`
 * answer, `ack`); section 2's principles 1 and 2 - the TD's TMS login never leaves his browser, and
 * nothing reaches TMS without a click inside the extension. The page ASKS; the extension shows the
 * plan in its own window and posts only on the TD's Confirm there.
 *
 * Four calls, and nothing else:
 *
 *   hello()          `{ type: hello, version: 1 }` -> `{ ok: true, extensionVersion, protocol: 1 }`,
 *                    within one second when the page looks on its own (as it opens, and when the
 *                    window regains the focus), within three at the press itself - a worker Chrome
 *                    has let sleep takes a moment to wake. No runtime, no real extension id, no
 *                    answer or an answer in another shape: the extension is ABSENT, and the page
 *                    prepares no press (section 4, step 1).
 *   push(plan, ...)  over a port (`chrome.runtime.connect`): the plan goes out once; progress
 *                    messages come back, then ONE result - or, instead, the extension's `refused`
 *                    (busy, invalid-message, already-finished, internal). The port closing first
 *                    is a LOST push, which the page follows with `status`.
 *   status(pressId)  the result the extension kept for that press, `{ pending: true }` while it
 *                    runs, or `{ unknown: true }`.
 *   ack(pressId)     the API has recorded this press's report: the extension may forget its copy.
 *
 * **Every message coming back is input from another program**, and is read as such: exact keys at
 * every level, every type, every bound. A result is held to the API's own rules through the one
 * reading the extension uses too (`readTmsPushResultMessage`, `@hockeytd/shared` - held to the
 * API's `parseExtensionResult` by a parity test in the API's workspace), so a result this page
 * accepts is one the API will not refuse for its shape; one it cannot read is REJECTED here - never
 * passed on as a result, never repaired, never reported. What IS accepted is handed on as the very
 * object the extension sent: the page reports it unchanged.
 *
 * **What the page knows for certain about delivery.** The plan never reached the extension when
 * there was no runtime or no real id, when the port could not even be opened or written to, or
 * when the extension answered `refused` for busy or invalid-message - and only then may the page
 * release the press (tmsPushFlow.ts). A port that closes, an `internal` refusal or an
 * `already-finished` one (the extension HAS run that press) is NOT such a case: the extension may
 * have posted, so the page asks `status`.
 *
 * **The extension's id** is {@link TMS_PUSH_EXTENSION_ID}: the production build's stable id, which
 * Chrome derives from the public key in its manifest (apps/extension/src/extension-id.ts).
 *
 * **The day push** (section 6c; the TD's Decision #5 = B, 19 Sep 2026) adds one call, `pushDay`, and
 * one message, `push-day`: over a port, as `push`, `{ type: push-day, version: 1, day, plans }` - every
 * competition's plan, in the send order - and back, progress messages that also carry the `pressId`
 * the stage belongs to, then ONE `result` per press IN PLAN ORDER, each read exactly as `push` reads
 * its one, against its own press id and owed matches. A day push is ONE push for the one-at-a-time
 * rule, here as in the extension. The message is at most 1 MiB of JSON text (section 6c), or it is
 * never sent.
 *
 * **The report batch** (2 Oct 2026, the TD's request: every ready technical official in one action)
 * adds `pushReportBatch` and `push-report-batch`: the day push's port, carrying report plans - one
 * report result per press IN PLAN ORDER, each read by the API's own reading
 * (`readTmsReportPushResultMessage`) against its own press id, progress naming its press, the port
 * closed after the last. The same 1 MiB bound, and ONE push for the one-at-a-time rule.
 */

import {
  TMS_PUSH_RESULT_TYPE,
  TMS_PUSH_RESULT_VERSION,
  TMS_REPORT_PUSH_RESULT_TYPE,
  readTmsPushResultMessage,
  readTmsReportPushResultMessage,
  type TmsReportPushPlan,
  type TmsReportPushResultMessage,
} from "@hockeytd/shared";

import type { ExtensionPushPlanBody, ExtensionPushResultMessage } from "../../api/types.js";

/* -- The id and the protocol ------------------------------------------------------------------ */

/**
 * THE TMS PUSH EXTENSION'S ID - the production build's, stable because its manifest carries a
 * public key and Chrome derives the id from the key (apps/extension/src/extension-id.ts, which a
 * test recomputes from the key). A Chrome extension id is 32 letters `a`-`p`.
 */
export const TMS_PUSH_EXTENSION_ID: string = "hmjpnanoblopgibhoanpcjibelofjcnk";

/** A Chrome extension id: 32 letters from `a` to `p`. */
const CHROME_EXTENSION_ID = /^[a-p]{32}$/;

/** Whether `id` can name an extension at all. */
export function isExtensionId(id: string): boolean {
  return CHROME_EXTENSION_ID.test(id);
}

/** The one protocol this page speaks (section 6). */
export const TMS_PUSH_PROTOCOL_VERSION = TMS_PUSH_RESULT_VERSION;

/** The message types of protocol version 1, verbatim from sections 6 and 6b. */
export const TMS_PUSH_MESSAGE_TYPES = {
  hello: "hockeytd.tms-push.hello",
  push: "hockeytd.tms-push.push",
  /** The day push (section 6c): every competition of the day, one Confirm, over one port. */
  pushDay: "hockeytd.tms-push.push-day",
  /** RUN 96 - one official's report, with its marks, into TMS's marking form as a draft. */
  pushReport: "hockeytd.tms-push.push-report",
  /** 2 Oct 2026 - every ready technical official's report press, behind ONE Confirm. */
  pushReportBatch: "hockeytd.tms-push.push-report-batch",
  reportResult: TMS_REPORT_PUSH_RESULT_TYPE,
  progress: "hockeytd.tms-push.progress",
  result: TMS_PUSH_RESULT_TYPE,
  refused: "hockeytd.tms-push.refused",
  status: "hockeytd.tms-push.status",
  ack: "hockeytd.tms-push.ack",
} as const;

/**
 * Section 6c: "The whole message at most 1 MiB of JSON text." The page never sends a `push-day`
 * message longer than this - counted as the extension counts it, in characters of `JSON.stringify` -
 * so it is never one the extension must refuse for its size.
 */
export const TMS_PUSH_DAY_MESSAGE_MAX_CHARS = 1024 * 1024;

/** A client-minted uuid, as the API reads a press id (any case; lower-cased when compared). */
const PRESS_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Section 4, step 1: no answer within one second means no extension - when the page looks on its own. */
export const TMS_PUSH_HELLO_TIMEOUT_MS = 1_000;

/**
 * At the press itself `hello` is given three seconds: Chrome stops an idle extension worker, and
 * the first message wakes it, which can take longer than the passive check's one second.
 */
export const TMS_PUSH_PRESS_HELLO_TIMEOUT_MS = 3_000;

/** How long `status` and `ack` wait for the extension's answer (section 6b: it answers within 3 s). */
export const TMS_PUSH_STATUS_TIMEOUT_MS = 3_000;

/** The progress stages a push reports, in the order they happen (section 6). */
export const TMS_PUSH_STAGES = ["awaiting-confirm", "reading", "posting", "verifying"] as const;

export type TmsPushStage = (typeof TMS_PUSH_STAGES)[number];

/** Why the extension refused a push on its port (section 6b). */
export const TMS_PUSH_REFUSAL_REASONS = ["invalid-message", "busy", "already-finished", "internal"] as const;

export type TmsPushRefusalReason = (typeof TMS_PUSH_REFUSAL_REASONS)[number];

/** The longest refusal detail the page will read (the extension's are a sentence or two). */
const REFUSAL_DETAIL_MAX = 2_000;

/** What `chrome-extension/{extensionVersion}` may carry in a hello: the extension's version. */
const EXTENSION_VERSION = /^[0-9A-Za-z][0-9A-Za-z.+_-]{0,31}$/;

/* -- Reading what comes back -------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

/** Every required key present, and no key that is neither required nor optional. */
function hasExactly(
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = [],
): boolean {
  return (
    required.every((key) => hasOwn(value, key)) &&
    Object.keys(value).every((key) => required.includes(key) || optional.includes(key))
  );
}

/** What reading a result message came to. */
export type TmsPushResultReading =
  | { readonly ok: true; readonly result: ExtensionPushResultMessage }
  | { readonly ok: false; readonly why: string };

/**
 * Read one result message (section 6) exactly as the API's report reads it
 * (`readTmsPushResultMessage`), and for THIS press: the press id must be the one asked about, and -
 * where the page knows the plan - every match it names must be one the plan owes, as the report
 * requires once it has found the press.
 *
 * On success the `result` is the very object that was read - not a copy, not a repair - so what
 * the page reports is what the extension sent.
 */
export function readTmsPushResult(
  raw: unknown,
  expect: { readonly pressId: string; readonly planned?: readonly number[] | null },
): TmsPushResultReading {
  const reading = readTmsPushResultMessage(raw, expect.pressId, expect.planned ?? null);
  return reading.ok ? { ok: true, result: reading.message } : { ok: false, why: reading.problem };
}

/** The extension's answer to `hello`, or null when it is not one (section 6). */
export function readTmsPushHello(raw: unknown): { readonly extensionVersion: string } | null {
  if (!isRecord(raw) || !hasExactly(raw, ["ok", "extensionVersion", "protocol"])) return null;
  if (raw["ok"] !== true || raw["protocol"] !== TMS_PUSH_PROTOCOL_VERSION) return null;
  const version = raw["extensionVersion"];
  if (typeof version !== "string" || !EXTENSION_VERSION.test(version)) return null;
  return { extensionVersion: version };
}

/** A progress message's stage, or null when the message is not a progress message it can read. */
export function readTmsPushProgress(raw: unknown): TmsPushStage | null {
  if (!isRecord(raw) || !hasExactly(raw, ["type", "stage"])) return null;
  if (raw["type"] !== TMS_PUSH_MESSAGE_TYPES.progress) return null;
  const stage = raw["stage"];
  return typeof stage === "string" && (TMS_PUSH_STAGES as readonly string[]).includes(stage)
    ? (stage as TmsPushStage)
    : null;
}

/**
 * The day push (section 6c) - a progress message on a `push-day` port: its stage AND the press it
 * belongs to (`awaiting-confirm` carries the first press's id). Null when the message is not one it can
 * read - a single push's progress, with no `pressId`, included: on a day port it says nothing usable.
 */
export function readTmsPushDayProgress(
  raw: unknown,
): { readonly stage: TmsPushStage; readonly pressId: string } | null {
  if (!isRecord(raw) || !hasExactly(raw, ["type", "stage", "pressId"])) return null;
  if (raw["type"] !== TMS_PUSH_MESSAGE_TYPES.progress) return null;
  const stage = raw["stage"];
  const pressId = raw["pressId"];
  if (typeof stage !== "string" || !(TMS_PUSH_STAGES as readonly string[]).includes(stage)) return null;
  if (typeof pressId !== "string" || !PRESS_ID.test(pressId)) return null;
  return { stage: stage as TmsPushStage, pressId: pressId.toLowerCase() };
}

/**
 * The extension's refusal of a push (section 6b), or null when the message is not one it can read.
 * `detail` is the extension's own sentence, which the page shows as text.
 */
export function readTmsPushRefusal(
  raw: unknown,
): { readonly reason: TmsPushRefusalReason; readonly detail: string } | null {
  if (!isRecord(raw) || !hasExactly(raw, ["type", "version", "reason", "detail"])) return null;
  if (raw["type"] !== TMS_PUSH_MESSAGE_TYPES.refused || raw["version"] !== TMS_PUSH_PROTOCOL_VERSION) {
    return null;
  }
  const reason = raw["reason"];
  const detail = raw["detail"];
  if (typeof reason !== "string" || !(TMS_PUSH_REFUSAL_REASONS as readonly string[]).includes(reason)) {
    return null;
  }
  if (typeof detail !== "string" || detail.trim() === "" || detail.length > REFUSAL_DETAIL_MAX) return null;
  return { reason: reason as TmsPushRefusalReason, detail };
}

/* -- What the four calls answer ----------------------------------------------------------------- */

/** Whether the extension is there to be asked, and which version answered. */
export type TmsPushPresence =
  | { readonly kind: "present"; readonly extensionVersion: string }
  | {
      readonly kind: "absent";
      /**
       * `no_runtime` - no `chrome.runtime` on this page (not Chrome, or no extension lets this
       * origin in); `no_extension_id` - the id is not an extension id; `no_answer` - nothing
       * within the second; `unreadable` - an answer that is not protocol version 1's.
       */
      readonly why: "no_runtime" | "no_extension_id" | "no_answer" | "unreadable";
    };

/** How a push over the port ended. */
export type TmsPushPortEnding =
  /** One result, read and accepted - the very object the extension sent. */
  | { readonly kind: "result"; readonly result: ExtensionPushResultMessage }
  /** A result message that could not be read. It is not passed on, and must not be reported. */
  | { readonly kind: "unreadable"; readonly why: string }
  /**
   * The extension refused the push on its port (section 6b), in its own words. For
   * `invalid-message` and `busy` it did not take the plan. `already-finished` says it HAS run this
   * press, and `internal` is a failure while taking it, after which it may hold the plan - for
   * both the page asks `status`.
   */
  | { readonly kind: "refused"; readonly reason: TmsPushRefusalReason; readonly detail: string }
  /** The port closed before any result: the page asks `status`. */
  | { readonly kind: "lost" }
  /** The page stopped waiting (its signal was aborted); the port was closed. */
  | { readonly kind: "stopped" }
  /** A push from this page is already out: this one was never sent (section 6: one at a time). */
  | { readonly kind: "busy" }
  /**
   * Nothing was sent: no runtime, no real id, or the port could not be opened or written to - the
   * extension was absent after all.
   */
  | { readonly kind: "unavailable" };

/**
 * The day push (section 6c) - how a `push-day` port ended. Every result read before the ending was
 * handed on as it arrived ({@link TmsPushDayListener.result}); the ending says what the page knows of
 * the rest.
 */
export type TmsPushDayPortEnding =
  /** One result for every plan, in plan order, each read and handed on. */
  | { readonly kind: "done" }
  /**
   * The extension refused the day push on its port, in its own words, as its FIRST word - before any
   * other message of its own, readable or not (fix round 1, P4). A refusal after one is read as a lost
   * port: the extension had the day push in hand, so what it refused is not the plans.
   */
  | { readonly kind: "refused"; readonly reason: TmsPushRefusalReason; readonly detail: string }
  /** The port closed before every result: the page asks `status` for each press still without one. */
  | { readonly kind: "lost" }
  /**
   * A message the page could not read - a result not for the next press in plan order, or one off
   * the rules. Nothing more is read from the port, and the page asks `status` for each press still
   * without a result - which answers from the extension's own storage, press by press.
   */
  | { readonly kind: "unreadable"; readonly why: string }
  /** The page stopped waiting (its signal was aborted); the port was closed. */
  | { readonly kind: "stopped" }
  /** A push from this page is already out: this one was never sent (one at a time). */
  | { readonly kind: "busy" }
  /** Nothing was sent: no runtime, no real id, or the port could not be opened or written to. */
  | { readonly kind: "unavailable" }
  /** Nothing was sent: the message would be longer than section 6c's 1 MiB of JSON text. */
  | { readonly kind: "too-large" };

/** What a `push-day` port hands on as it runs (section 6c). */
export interface TmsPushDayListener {
  /** A stage of the press `pressId` - one of this day push's own, lower-cased. */
  progress(stage: TmsPushStage, pressId: string): void;
  /** The result of the press at `index` in plan order - the very object the extension sent. */
  result(index: number, result: ExtensionPushResultMessage): void;
}

/** How a report push over the port ended (RUN 96) - the single push's endings, with a report result. */
export type TmsReportPushPortEnding =
  | { readonly kind: "result"; readonly result: TmsReportPushResultMessage }
  | Exclude<TmsPushPortEnding, { readonly kind: "result" }>;

/** What a `push-report-batch` port hands on as it runs (2 Oct 2026). */
export interface TmsReportBatchListener {
  /** A stage of the press `pressId` - one of this batch's own, lower-cased. */
  progress(stage: TmsPushStage, pressId: string): void;
  /** The result of the press at `index` in plan order - the very object the extension sent. */
  result(index: number, result: TmsReportPushResultMessage): void;
}

/** How a `push-report-batch` port ended - the day push's endings, word for word. */
export type TmsReportBatchPortEnding = TmsPushDayPortEnding;

/** What the extension kept for a report press (RUN 96). */
export type TmsReportPushStatus =
  | { readonly kind: "result"; readonly result: TmsReportPushResultMessage }
  | { readonly kind: "pending" }
  | { readonly kind: "unknown" }
  | { readonly kind: "unreadable"; readonly why: string }
  | { readonly kind: "no_answer" };

/** What the extension kept for a press. */
export type TmsPushStatus =
  | { readonly kind: "result"; readonly result: ExtensionPushResultMessage }
  | { readonly kind: "pending" }
  | { readonly kind: "unknown" }
  | { readonly kind: "unreadable"; readonly why: string }
  | { readonly kind: "no_answer" };

/** What the extension did with an `ack`. */
export type TmsPushAck =
  /** It had the result, and has forgotten it. */
  | { readonly kind: "forgotten" }
  /** It held no result for the press (none was kept, or it was already forgotten). */
  | { readonly kind: "unknown" }
  | { readonly kind: "unreadable" }
  | { readonly kind: "no_answer" };

export interface TmsPushBridge {
  /**
   * Synchronous and without a message: whether there is anything to ask at all - a runtime on the
   * page and a real extension id. False means absent, with no need to wait a second to know it.
   */
  reachable(): boolean;
  /** `timeoutMs`: how long to wait for the answer - the passive one second by default. */
  hello(timeoutMs?: number): Promise<TmsPushPresence>;
  push(
    plan: ExtensionPushPlanBody,
    onProgress: (stage: TmsPushStage) => void,
    signal?: AbortSignal,
  ): Promise<TmsPushPortEnding>;
  /**
   * The day push (section 6c): every competition's plan of `day`, in the send order, over one port.
   * The live bridge always has it. Optional only so that a bridge made for the single press alone - a
   * test's stand-in - is still one: the page treats a bridge without it as no extension to push a day
   * through, and prepares nothing.
   */
  pushDay?(
    day: string,
    plans: readonly ExtensionPushPlanBody[],
    on: TmsPushDayListener,
    signal?: AbortSignal,
  ): Promise<TmsPushDayPortEnding>;
  /**
   * RUN 96 - a report press over a port: the plan goes out once; progress comes back, then ONE
   * report result - read by the API's own reading, `readTmsReportPushResultMessage` - or the
   * extension's refusal. Optional so a bridge made for the day push alone is still one: the page
   * treats a bridge without it as no extension to push a report through.
   */
  pushReport?(plan: TmsReportPushPlan, onProgress: (stage: TmsPushStage) => void, signal?: AbortSignal): Promise<TmsReportPushPortEnding>;
  /** RUN 96 - what the extension kept for a report press. */
  reportStatus?(pressId: string): Promise<TmsReportPushStatus>;
  /**
   * 2 Oct 2026 - a report batch over one port: every plan in the send order; progress naming its
   * press, then ONE report result per press in plan order. Optional, as `pushReport` is: a bridge
   * without it is no extension to push a batch through.
   */
  pushReportBatch?(plans: readonly TmsReportPushPlan[], on: TmsReportBatchListener, signal?: AbortSignal): Promise<TmsReportBatchPortEnding>;
  /** `planned`: the match ids the press owes, where the page knows them, to hold a result to. */
  status(pressId: string, planned?: readonly number[] | null): Promise<TmsPushStatus>;
  /** Only once the API has recorded the press's report: the extension may drop its copy. */
  ack(pressId: string): Promise<TmsPushAck>;
}

/* -- The runtime, as far as this page uses it --------------------------------------------------- */

interface RuntimeEvent<Listener> {
  addListener(listener: Listener): void;
  removeListener?(listener: Listener): void;
}

/** A `chrome.runtime.Port`, as far as the bridge uses one. */
export interface TmsPushPort {
  postMessage(message: unknown): void;
  disconnect(): void;
  readonly onMessage: RuntimeEvent<(message: unknown) => void>;
  readonly onDisconnect: RuntimeEvent<() => void>;
}

/** `chrome.runtime` as a web page sees it (`externally_connectable`), as far as the bridge uses it. */
export interface TmsPushRuntime {
  sendMessage(extensionId: string, message: unknown, callback: (response: unknown) => void): void;
  connect(extensionId: string, connectInfo?: { name?: string }): TmsPushPort;
  readonly lastError?: unknown;
}

/**
 * The page's own `chrome.runtime`, or null. Chrome puts one on a page only when some installed
 * extension lets that origin message it, so its absence is the commonest "not installed".
 */
function pageRuntime(): TmsPushRuntime | null {
  const chrome = (globalThis as { chrome?: { runtime?: unknown } }).chrome;
  const runtime = chrome?.runtime;
  if (typeof runtime !== "object" || runtime === null) return null;
  const candidate = runtime as Partial<TmsPushRuntime>;
  if (typeof candidate.sendMessage !== "function" || typeof candidate.connect !== "function") {
    return null;
  }
  return candidate as TmsPushRuntime;
}

/** Read - and so clear - `lastError`. True when the call it follows failed. */
function failedCall(runtime: TmsPushRuntime): boolean {
  try {
    return runtime.lastError !== undefined && runtime.lastError !== null;
  } catch {
    return true;
  }
}

type Answer = { readonly answered: true; readonly value: unknown } | { readonly answered: false };

/** One message, one answer, within `timeoutMs` - or no answer. Never throws. */
function ask(
  runtime: TmsPushRuntime,
  extensionId: string,
  message: unknown,
  timeoutMs: number,
): Promise<Answer> {
  return new Promise<Answer>((resolve) => {
    let settled = false;
    const settle = (answer: Answer): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(answer);
    };
    const timer = setTimeout(() => {
      settle({ answered: false });
    }, timeoutMs);
    try {
      runtime.sendMessage(extensionId, message, (response: unknown) => {
        // `lastError` is read on every answer, so an extension that is not there is a quiet "no
        // answer" rather than an unchecked error in the console.
        const failed = failedCall(runtime);
        settle(failed ? { answered: false } : { answered: true, value: response });
      });
    } catch {
      settle({ answered: false });
    }
  });
}

export interface TmsPushBridgeOptions {
  /** Where the runtime comes from. The page's own `chrome.runtime` by default. */
  readonly runtime?: () => TmsPushRuntime | null;
  /** {@link TMS_PUSH_EXTENSION_ID} by default. */
  readonly extensionId?: string;
  readonly helloTimeoutMs?: number;
  readonly statusTimeoutMs?: number;
}

/** The bridge. `options` exist for tests; the page uses {@link LIVE_TMS_PUSH_BRIDGE}. */
export function createTmsPushBridge(options: TmsPushBridgeOptions = {}): TmsPushBridge {
  const runtimeOf = options.runtime ?? pageRuntime;
  const extensionId = options.extensionId ?? TMS_PUSH_EXTENSION_ID;
  const helloTimeoutMs = options.helloTimeoutMs ?? TMS_PUSH_HELLO_TIMEOUT_MS;
  const statusTimeoutMs = options.statusTimeoutMs ?? TMS_PUSH_STATUS_TIMEOUT_MS;
  /** One push from this page at a time, as the extension runs one at a time (section 6). */
  let portOut = false;

  /** The runtime to use, or null when there is nobody to ask: no runtime, or no real id. */
  function target(): TmsPushRuntime | null {
    if (!isExtensionId(extensionId)) return null;
    return runtimeOf();
  }

  return {
    reachable(): boolean {
      return target() !== null;
    },

    async hello(timeoutMs = helloTimeoutMs): Promise<TmsPushPresence> {
      if (!isExtensionId(extensionId)) return { kind: "absent", why: "no_extension_id" };
      const runtime = runtimeOf();
      if (runtime === null) return { kind: "absent", why: "no_runtime" };
      const answer = await ask(
        runtime,
        extensionId,
        { type: TMS_PUSH_MESSAGE_TYPES.hello, version: TMS_PUSH_PROTOCOL_VERSION },
        timeoutMs,
      );
      if (!answer.answered) return { kind: "absent", why: "no_answer" };
      const hello = readTmsPushHello(answer.value);
      if (hello === null) return { kind: "absent", why: "unreadable" };
      return { kind: "present", extensionVersion: hello.extensionVersion };
    },

    push(plan, onProgress, signal): Promise<TmsPushPortEnding> {
      const runtime = target();
      if (runtime === null) return Promise.resolve({ kind: "unavailable" });
      if (portOut) return Promise.resolve({ kind: "busy" });
      if (signal?.aborted === true) return Promise.resolve({ kind: "stopped" });
      portOut = true;
      const planned = plan.payloads.map((payload) => payload.tmsMatchDbId);

      return new Promise<TmsPushPortEnding>((resolve) => {
        let port: TmsPushPort | null = null;
        let done = false;

        const onMessage = (message: unknown): void => {
          if (done) return;
          const stage = readTmsPushProgress(message);
          if (stage !== null) {
            onProgress(stage);
            return;
          }
          if (!isRecord(message)) return;
          // A result is the last word, read or not.
          if (message["type"] === TMS_PUSH_MESSAGE_TYPES.result) {
            const reading = readTmsPushResult(message, { pressId: plan.pressId, planned });
            finish(
              reading.ok
                ? { kind: "result", result: reading.result }
                : { kind: "unreadable", why: reading.why },
            );
            return;
          }
          // So is a refusal. One the page cannot read says nothing it can rely on about the plan:
          // it is taken as a lost port, and the page asks `status`.
          if (message["type"] === TMS_PUSH_MESSAGE_TYPES.refused) {
            const refusal = readTmsPushRefusal(message);
            finish(refusal === null ? { kind: "lost" } : { kind: "refused", ...refusal });
          }
          // Anything else is not acted on at all.
        };
        const onDisconnect = (): void => {
          failedCall(runtime);
          finish({ kind: "lost" });
        };
        const onAbort = (): void => {
          finish({ kind: "stopped" });
        };

        function finish(ending: TmsPushPortEnding): void {
          if (done) return;
          done = true;
          portOut = false;
          signal?.removeEventListener("abort", onAbort);
          if (port !== null) {
            port.onMessage.removeListener?.(onMessage);
            port.onDisconnect.removeListener?.(onDisconnect);
            try {
              port.disconnect();
            } catch {
              /* Already closed. */
            }
          }
          resolve(ending);
        }

        signal?.addEventListener("abort", onAbort, { once: true });
        try {
          port = runtime.connect(extensionId, { name: "hockeytd.tms-push" });
          port.onMessage.addListener(onMessage);
          port.onDisconnect.addListener(onDisconnect);
        } catch {
          // No port: the plan never left this page.
          finish({ kind: "unavailable" });
          return;
        }
        try {
          port.postMessage({
            type: TMS_PUSH_MESSAGE_TYPES.push,
            version: TMS_PUSH_PROTOCOL_VERSION,
            plan,
          });
        } catch {
          // Refused by the port itself, synchronously: the plan was not sent.
          finish({ kind: "unavailable" });
        }
      });
    },

    pushDay(day, plans, on, signal): Promise<TmsPushDayPortEnding> {
      const runtime = target();
      if (runtime === null) return Promise.resolve({ kind: "unavailable" });
      // ONE push for the one-at-a-time rule: a day push and a single push share the one port.
      if (portOut) return Promise.resolve({ kind: "busy" });
      if (signal?.aborted === true) return Promise.resolve({ kind: "stopped" });
      const message = {
        type: TMS_PUSH_MESSAGE_TYPES.pushDay,
        version: TMS_PUSH_PROTOCOL_VERSION,
        day,
        plans,
      };
      // Section 6c's bound, counted as the extension counts it: over it, the message never leaves
      // this page - and so, certainly, never reached the extension. (Plans that cannot even be
      // written as JSON are not a message at all, and are never sent either.)
      let size: number;
      try {
        size = JSON.stringify(message).length;
      } catch {
        return Promise.resolve({ kind: "too-large" });
      }
      if (size > TMS_PUSH_DAY_MESSAGE_MAX_CHARS) return Promise.resolve({ kind: "too-large" });
      portOut = true;
      // Each press, in plan order: the result the port must bring next is the NEXT press's.
      const expected = plans.map((plan) => ({
        pressId: plan.pressId.toLowerCase(),
        planned: plan.payloads.map((payload) => payload.tmsMatchDbId),
      }));
      const ours = new Set(expected.map((entry) => entry.pressId));

      return new Promise<TmsPushDayPortEnding>((resolve) => {
        let port: TmsPushPort | null = null;
        let done = false;
        /** Whether the extension has said anything but a refusal: then it has taken the day push. */
        let heard = false;
        let next = 0;

        const onMessage = (raw: unknown): void => {
          if (done) return;
          // Fix round 1 (P4): ANY message the extension sends with a type of its own, other than the
          // refusal itself - a stage this page cannot read, a stage of another press, anything - says
          // it has the day push in hand, so a refusal after it is never proof the plans were not
          // taken, and never releases a press.
          if (isRecord(raw) && typeof raw["type"] === "string" && raw["type"] !== TMS_PUSH_MESSAGE_TYPES.refused) {
            heard = true;
          }
          const progress = readTmsPushDayProgress(raw);
          if (progress !== null) {
            // A stage of a press that is not this day push's own is not acted on at all.
            if (!ours.has(progress.pressId)) return;
            try {
              on.progress(progress.stage, progress.pressId);
            } catch {
              /* The page's listener failed; the port is read on regardless. */
            }
            return;
          }
          if (!isRecord(raw)) return;
          if (raw["type"] === TMS_PUSH_MESSAGE_TYPES.result) {
            const expect = expected[next];
            if (expect === undefined) {
              finish({ kind: "unreadable", why: "a result came after every press of the day push had one" });
              return;
            }
            // Exactly the single push's rules, against this press's own id and owed matches: a
            // result for another press - out of plan order - is not this one's, and ends the reading.
            const reading = readTmsPushResult(raw, { pressId: expect.pressId, planned: expect.planned });
            if (!reading.ok) {
              finish({ kind: "unreadable", why: reading.why });
              return;
            }
            const index = next;
            next += 1;
            try {
              on.result(index, reading.result);
            } catch {
              /* As above: what was read is handed on; a listener's failure is not the port's. */
            }
            if (next === expected.length) finish({ kind: "done" });
            return;
          }
          if (raw["type"] === TMS_PUSH_MESSAGE_TYPES.refused) {
            const refusal = readTmsPushRefusal(raw);
            // A refusal is proof the day push was not taken only as the extension's FIRST word; one it
            // cannot read, or one after any other message of the extension's, is a lost port.
            finish(refusal === null || heard ? { kind: "lost" } : { kind: "refused", ...refusal });
          }
          // Anything else is not acted on at all.
        };
        const onDisconnect = (): void => {
          failedCall(runtime);
          finish({ kind: "lost" });
        };
        const onAbort = (): void => {
          finish({ kind: "stopped" });
        };

        function finish(ending: TmsPushDayPortEnding): void {
          if (done) return;
          done = true;
          portOut = false;
          signal?.removeEventListener("abort", onAbort);
          if (port !== null) {
            port.onMessage.removeListener?.(onMessage);
            port.onDisconnect.removeListener?.(onDisconnect);
            try {
              port.disconnect();
            } catch {
              /* Already closed. */
            }
          }
          resolve(ending);
        }

        signal?.addEventListener("abort", onAbort, { once: true });
        try {
          port = runtime.connect(extensionId, { name: "hockeytd.tms-push" });
          port.onMessage.addListener(onMessage);
          port.onDisconnect.addListener(onDisconnect);
        } catch {
          // No port: the day push never left this page.
          finish({ kind: "unavailable" });
          return;
        }
        try {
          port.postMessage(message);
        } catch {
          // Refused by the port itself, synchronously: the day push was not sent.
          finish({ kind: "unavailable" });
        }
      });
    },

    pushReport(plan, onProgress, signal): Promise<TmsReportPushPortEnding> {
      const runtime = target();
      if (runtime === null) return Promise.resolve({ kind: "unavailable" });
      if (portOut) return Promise.resolve({ kind: "busy" });
      if (signal?.aborted === true) return Promise.resolve({ kind: "stopped" });
      portOut = true;
      return new Promise<TmsReportPushPortEnding>((resolve) => {
        let port: TmsPushPort | null = null;
        let done = false;
        const onMessage = (message: unknown): void => {
          if (done) return;
          const stage = readTmsPushProgress(message);
          if (stage !== null) {
            onProgress(stage);
            return;
          }
          if (!isRecord(message)) return;
          if (message["type"] === TMS_PUSH_MESSAGE_TYPES.reportResult) {
            const reading = readTmsReportPushResultMessage(message, plan.pressId);
            finish(reading.ok ? { kind: "result", result: reading.message } : { kind: "unreadable", why: reading.problem });
            return;
          }
          if (message["type"] === TMS_PUSH_MESSAGE_TYPES.refused) {
            const refusal = readTmsPushRefusal(message);
            finish(refusal === null ? { kind: "lost" } : { kind: "refused", ...refusal });
          }
        };
        const onDisconnect = (): void => {
          failedCall(runtime);
          finish({ kind: "lost" });
        };
        const onAbort = (): void => {
          finish({ kind: "stopped" });
        };
        function finish(ending: TmsReportPushPortEnding): void {
          if (done) return;
          done = true;
          portOut = false;
          signal?.removeEventListener("abort", onAbort);
          if (port !== null) {
            port.onMessage.removeListener?.(onMessage);
            port.onDisconnect.removeListener?.(onDisconnect);
            try {
              port.disconnect();
            } catch {
              /* Already closed. */
            }
          }
          resolve(ending);
        }
        signal?.addEventListener("abort", onAbort, { once: true });
        try {
          port = runtime.connect(extensionId, { name: "hockeytd.tms-push" });
          port.onMessage.addListener(onMessage);
          port.onDisconnect.addListener(onDisconnect);
        } catch {
          finish({ kind: "unavailable" });
          return;
        }
        try {
          port.postMessage({ type: TMS_PUSH_MESSAGE_TYPES.pushReport, version: TMS_PUSH_PROTOCOL_VERSION, plan });
        } catch {
          finish({ kind: "unavailable" });
        }
      });
    },

    pushReportBatch(plans, on, signal): Promise<TmsReportBatchPortEnding> {
      const runtime = target();
      if (runtime === null) return Promise.resolve({ kind: "unavailable" });
      if (portOut) return Promise.resolve({ kind: "busy" });
      if (signal?.aborted === true) return Promise.resolve({ kind: "stopped" });
      const message = { type: TMS_PUSH_MESSAGE_TYPES.pushReportBatch, version: TMS_PUSH_PROTOCOL_VERSION, plans };
      let size: number;
      try {
        size = JSON.stringify(message).length;
      } catch {
        return Promise.resolve({ kind: "too-large" });
      }
      if (size > TMS_PUSH_DAY_MESSAGE_MAX_CHARS) return Promise.resolve({ kind: "too-large" });
      portOut = true;
      const expected = plans.map((plan) => plan.pressId.toLowerCase());
      const ours = new Set(expected);

      return new Promise<TmsReportBatchPortEnding>((resolve) => {
        let port: TmsPushPort | null = null;
        let done = false;
        /** Whether the extension has said anything but a refusal: then it has taken the batch. */
        let heard = false;
        let next = 0;

        const onMessage = (raw: unknown): void => {
          if (done) return;
          if (isRecord(raw) && typeof raw["type"] === "string" && raw["type"] !== TMS_PUSH_MESSAGE_TYPES.refused) heard = true;
          const progress = readTmsPushDayProgress(raw);
          if (progress !== null) {
            if (!ours.has(progress.pressId)) return;
            try {
              on.progress(progress.stage, progress.pressId);
            } catch {
              /* The page's listener failed; the port is read on regardless. */
            }
            return;
          }
          if (!isRecord(raw)) return;
          if (raw["type"] === TMS_PUSH_MESSAGE_TYPES.reportResult) {
            const expect = expected[next];
            if (expect === undefined) {
              finish({ kind: "unreadable", why: "a result came after every press of the batch had one" });
              return;
            }
            // The API's own reading, against THIS press's id: a result out of plan order is not its.
            const reading = readTmsReportPushResultMessage(raw, expect);
            if (!reading.ok) {
              finish({ kind: "unreadable", why: reading.problem });
              return;
            }
            const index = next;
            next += 1;
            try {
              on.result(index, reading.message);
            } catch {
              /* As above. */
            }
            if (next === expected.length) finish({ kind: "done" });
            return;
          }
          if (raw["type"] === TMS_PUSH_MESSAGE_TYPES.refused) {
            const refusal = readTmsPushRefusal(raw);
            finish(refusal === null || heard ? { kind: "lost" } : { kind: "refused", ...refusal });
          }
        };
        const onDisconnect = (): void => {
          failedCall(runtime);
          finish({ kind: "lost" });
        };
        const onAbort = (): void => {
          finish({ kind: "stopped" });
        };
        function finish(ending: TmsReportBatchPortEnding): void {
          if (done) return;
          done = true;
          portOut = false;
          signal?.removeEventListener("abort", onAbort);
          if (port !== null) {
            port.onMessage.removeListener?.(onMessage);
            port.onDisconnect.removeListener?.(onDisconnect);
            try {
              port.disconnect();
            } catch {
              /* Already closed. */
            }
          }
          resolve(ending);
        }
        signal?.addEventListener("abort", onAbort, { once: true });
        try {
          port = runtime.connect(extensionId, { name: "hockeytd.tms-push" });
          port.onMessage.addListener(onMessage);
          port.onDisconnect.addListener(onDisconnect);
        } catch {
          finish({ kind: "unavailable" });
          return;
        }
        try {
          port.postMessage(message);
        } catch {
          finish({ kind: "unavailable" });
        }
      });
    },

    async reportStatus(pressId): Promise<TmsReportPushStatus> {
      const runtime = target();
      if (runtime === null) return { kind: "no_answer" };
      const answer = await ask(runtime, extensionId, { type: TMS_PUSH_MESSAGE_TYPES.status, version: TMS_PUSH_PROTOCOL_VERSION, pressId }, statusTimeoutMs);
      if (!answer.answered) return { kind: "no_answer" };
      const value = answer.value;
      if (isRecord(value) && hasExactly(value, ["pending"]) && value["pending"] === true) return { kind: "pending" };
      if (isRecord(value) && hasExactly(value, ["unknown"]) && value["unknown"] === true) return { kind: "unknown" };
      const reading = readTmsReportPushResultMessage(value, pressId);
      return reading.ok ? { kind: "result", result: reading.message } : { kind: "unreadable", why: reading.problem };
    },

    async status(pressId, planned = null): Promise<TmsPushStatus> {
      const runtime = target();
      if (runtime === null) return { kind: "no_answer" };
      const answer = await ask(
        runtime,
        extensionId,
        { type: TMS_PUSH_MESSAGE_TYPES.status, version: TMS_PUSH_PROTOCOL_VERSION, pressId },
        statusTimeoutMs,
      );
      if (!answer.answered) return { kind: "no_answer" };
      const value = answer.value;
      if (isRecord(value) && hasExactly(value, ["pending"]) && value["pending"] === true) {
        return { kind: "pending" };
      }
      if (isRecord(value) && hasExactly(value, ["unknown"]) && value["unknown"] === true) {
        return { kind: "unknown" };
      }
      const reading = readTmsPushResult(value, { pressId, planned });
      return reading.ok
        ? { kind: "result", result: reading.result }
        : { kind: "unreadable", why: reading.why };
    },

    async ack(pressId): Promise<TmsPushAck> {
      const runtime = target();
      if (runtime === null) return { kind: "no_answer" };
      const answer = await ask(
        runtime,
        extensionId,
        { type: TMS_PUSH_MESSAGE_TYPES.ack, version: TMS_PUSH_PROTOCOL_VERSION, pressId },
        statusTimeoutMs,
      );
      if (!answer.answered) return { kind: "no_answer" };
      const value = answer.value;
      if (isRecord(value) && hasExactly(value, ["ok"]) && value["ok"] === true) return { kind: "forgotten" };
      if (isRecord(value) && hasExactly(value, ["ok", "unknown"]) && value["ok"] === true && value["unknown"] === true) {
        return { kind: "unknown" };
      }
      return { kind: "unreadable" };
    },
  };
}

/** The bridge the page uses: the page's own `chrome.runtime` and {@link TMS_PUSH_EXTENSION_ID}. */
export const LIVE_TMS_PUSH_BRIDGE: TmsPushBridge = createTmsPushBridge();
